import { Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import DeleteForever from '@mui/icons-material/DeleteForever';
import { useAppDispatch } from "../../../../app/hooks";
import { useState, useMemo } from "react";
import { AppId } from "../../../../app/appTypes";
import { Localized } from "@fluent/react";
import { deleteProcessTeamMember, ProjectProcessTeamMember } from "../ProjectProcessApiClient";
import { DeleteButton } from "../../../../app/components/DeleteButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";

export interface DeleteProcessTeamMemberDialogProps {
    teamMember: ProjectProcessTeamMember;
    onSave: (teamMemberId: AppId) => void;
    onCancel: () => void;
}

export const DeleteProcessTeamMemberDialog = ({
    teamMember,
    onSave,
    onCancel,
}: DeleteProcessTeamMemberDialogProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const fullName = useMemo(() => {
        return [teamMember.firstName, teamMember.surname].filter(Boolean).join(" ");
    }, [teamMember]);

    const handleConfirmDeleteClick = async () => {
        setSaving(true);
        try {
            const result = await dispatch(deleteProcessTeamMember(teamMember.id));

            if (deleteProcessTeamMember.fulfilled.match(result)) {
                dispatch(showSuccess("deleted"));
                onSave(teamMember.id);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open maxWidth="sm" onClose={onCancel} aria-labelledby="delete-team-member-title">
            <DialogTitle
                id="delete-team-member-title"
                sx={{
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                }}
            >
                <DeleteForever color="error" />
                <Localized id="delete-team-member-title">
                    <span>Usuń członka zespołu</span>
                </Localized>
            </DialogTitle>

            <DialogContent sx={{ mt: 3 }}>
                <DialogContentText>
                    <Localized
                        id="confirm-delete-team-member"
                        vars={{ name: fullName }}
                        elems={{ strong: <strong /> }}
                    >
                        <span />
                    </Localized>
                </DialogContentText>
                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <DeleteButton loading={saving} onClick={handleConfirmDeleteClick}></DeleteButton>
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
